import { Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";

import ReactGA from "react-ga4";

import axios from "helpers/axiosInstance";
import {
  LinkedInConversionId,
  trackLinkedInConversion,
} from "helpers/utility/linkedinAnalytics";
import { composeApiURL } from "helpers/utility/axiosHelpers";

import { State } from "redux/store";
import {
  RESET_STORE,
  SET_ACTIVITY_LOGS,
  SET_ACTIVITY_OFFSET,
  SET_ANNOTATION_STATUS,
  SET_CONNECT_STATUS,
  SET_IS_FATAL_ERROR,
  SET_IMAGE_ERROR_FILE_NAME,
  SET_NEW_DATASET,
  SET_PREVIEW_DATA,
  SET_PREVIEW_DATA_SOURCE,
  SET_S3_FETCH_PROGRESS,
  SET_UPLOAD_STATUS,
  SET_FILES_UPLOAD_TRANS_ID,
  SET_INDEXING_STATUS,
  SET_LIMIT_EXCEEDED_DETAILS,
  SET_ARE_ANNOTATIONS_FETCHED,
  SET_STATUS_CALL_IN_PROGRESS,
  SET_IS_LOADING,
  SET_SHOW_DELETE_DATASET_MODAL,
  SET_LAST_USED_DATA,
  SET_UPLOAD_FINISHED,
  SET_USER_INITIATED_CREATION,
  SET_FILES_UPLOAD_SIZE,
  SET_FILES_UPLOAD_PROGRESS,
  SET_SHOW_ACTIVITY_LOG_MODAL,
  SET_CVAT_DATA,
  SET_IS_CVAT_LOADING,
  // New Dataset Creation Workflow
  SET_FOLDER_UPLOAD_STATUS,
  SET_UPLOADING_FOLDER,
  SET_MULTI_FOLDER_UPLOAD,
  UPDATE_MULTI_FOLDER_PROGRESS,
  SET_FOLDERS_METADATA,
  SET_PREVIEWS,
  SET_PREVIEWS_LOADING,
  SET_FOLDER_METADATA_POLLING,
  SET_LOCAL_FOLDER_SELECTIONS,
  SET_INVALID_FOLDERS,
  // Dataset Processing
  SET_CREATION_STATUS,
  SET_CREATION_STATUS_POLLING,
  SET_VALIDATION_LOADING,
} from "./constants";
import {
  ActivityLogShowModalType,
  CVATTypes,
  DataPreview,
  DatasetStatusEnum,
  DataSources,
  FilesUploadSize,
  FolderUploadStatus,
  LastUsedData,
  RawCVATProject,
  RawCVATTask,
  StepTileProgressStatus,
  UploadingFolder,
  MultiFolderUploadState,
  FolderMetadata,
  CreationStatusResponse,
  SecondaryStatusItem,
  FolderStatus,
  BackendFolderStatus,
} from "types";
import { getRequest } from "helpers/utility/axiosServiceLayer";
import {
  DatasetInitialized,
  DataSourceEvent,
  Event,
  FileMediaPreview,
  LimitExceeded,
  S3MediaPreview,
} from "extractedBackendTypes";
import {
  getActivityLogs,
  getConnectStatus,
  getActivityOffset,
  getAnnotationStatus,
  getAreAnnotationsFetched,
  getFilesUploadTransId,
  getIsNewDatasetInitializing,
  getLastUsedData,
  getPreviewData,
  getPreviewDataSource,
  getStatusCallInProgress,
  getUserInitiatedCreation,
  getFilesUploadSize,
  getNewDataset,
  getIsNewCreationEnabled,
  getUploadingFolder,
  getFolderMetadataPolling,
  getFolderMetadata,
  getCreationStatus,
  getCreationStatusPolling,
  getIsNewDatasetReady,
} from "./selectors";
import { setLimitError } from "redux/Datasets/actions";
import { AxiosProgressEvent } from "axios";
import { setQuotaLimitModalData } from "redux/Modals/actions";
import { getIsUsageReportEnabled } from "redux/Datasets/selectors";
import { checkUserQuota } from "redux/User/actions";
import abortControllerManager from "helpers/abortControllerManager";
import {
  trackAnnotation,
  trackDataSourceAdded,
  trackFatalError,
  trackNewDatasetIngestionEvent,
} from "helpers/utility/amplitude";
import {
  AMP_DATASET_INGESTION_EVENT__DATASET_CREATION_INITIATED,
  AMP_DATASET_INGESTION_EVENT__DATASET_CREATION_SUCCESS,
  AMP_DATASET_INGESTION_EVENT__INDEXING_STARTED,
  AMP_DATASET_INGESTION_EVENT__QUOTA_LIMIT_EXCEEDED,
} from "helpers/constants/amplitudeEvents";
import {
  AMP_DATASET_CREATION_PROPERTY__ALLOWED_UPLOAD_COUNT,
  AMP_DATASET_CREATION_PROPERTY__ATTEMPTED_UPLOAD_COUNT,
  AMP_DATASET_CREATION_PROPERTY__UPLOAD_SOURCE,
} from "helpers/constants/amplitudeProperties";
import { getUserQuota } from "redux/User/selectors";
import { getEnrichedTargetDatasetId } from "redux/Enrichment/selectors";

const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

export const resetStore = () => {
  // Clear any active polling intervals
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }
  if (creationStatusPollingIntervalId) {
    clearInterval(creationStatusPollingIntervalId);
    creationStatusPollingIntervalId = null;
  }

  return { type: RESET_STORE };
};

export const setConnectStatus = (data: StepTileProgressStatus) => {
  return { type: SET_CONNECT_STATUS, data };
};

export const setAnnotationStatus = (data: StepTileProgressStatus) => {
  return { type: SET_ANNOTATION_STATUS, data };
};

export const setUploadStatus = (data: StepTileProgressStatus) => {
  return { type: SET_UPLOAD_STATUS, data };
};

export const setIndexingStatus = (data: StepTileProgressStatus) => {
  return { type: SET_INDEXING_STATUS, data };
};

export const setPreviewData = (data: DataPreview[]) => {
  return { type: SET_PREVIEW_DATA, data };
};

export const setPreviewDataSource = (data: DataSources | null) => {
  return { type: SET_PREVIEW_DATA_SOURCE, data };
};

export const setNewDataset = (data: DatasetInitialized | null) => {
  return { type: SET_NEW_DATASET, data };
};

export const setS3FetchProgress = (data: number) => {
  return { type: SET_S3_FETCH_PROGRESS, data };
};

export const setActivityLogs = (data: any[]) => {
  return { type: SET_ACTIVITY_LOGS, data };
};

export const setImageErrorFileName = (data: string) => {
  return { type: SET_IMAGE_ERROR_FILE_NAME, data };
};

export const setActivityOffset = (data: number) => {
  return { type: SET_ACTIVITY_OFFSET, data };
};

export const setIsFatalError = (data: boolean) => {
  return { type: SET_IS_FATAL_ERROR, data };
};

export const setFilesUploadTransId = (data: string | null) => {
  return { type: SET_FILES_UPLOAD_TRANS_ID, data };
};

export const setLimitExceededDetails = (data: LimitExceeded | null) => {
  return { type: SET_LIMIT_EXCEEDED_DETAILS, data };
};

export const setAreAnnotationsFetched = (data: boolean) => {
  return { type: SET_ARE_ANNOTATIONS_FETCHED, data };
};

export const setStatusCallInProgress = (data: boolean) => {
  return { type: SET_STATUS_CALL_IN_PROGRESS, data };
};

export const setIsLoading = (data: boolean) => {
  return { type: SET_IS_LOADING, data };
};

export const setShowDeleteDatasetModal = (data: boolean) => {
  return { type: SET_SHOW_DELETE_DATASET_MODAL, data };
};

export const setLastUsedData = (data: LastUsedData) => {
  return { type: SET_LAST_USED_DATA, data };
};

export const setUploadFinished = (data: boolean) => {
  return { type: SET_UPLOAD_FINISHED, data };
};

export const setUserInitiatedCreation = (data: boolean) => {
  return { type: SET_USER_INITIATED_CREATION, data };
};

export const setFilesUploadSize = (data: FilesUploadSize | null) => {
  return { type: SET_FILES_UPLOAD_SIZE, data };
};

export const setFilesUploadProgress = (data: number | null) => {
  return { type: SET_FILES_UPLOAD_PROGRESS, data };
};

export const setCVATData = (data: {
  tasks: RawCVATTask[];
  projects: RawCVATProject[];
}) => {
  return { type: SET_CVAT_DATA, data };
};

export const setIsCvatLoading = (data: boolean) => {
  return { type: SET_IS_CVAT_LOADING, data };
};

// New Dataset Creation Workflow Actions
export const setFolderUploadStatus = (data: FolderUploadStatus) => {
  return { type: SET_FOLDER_UPLOAD_STATUS, data };
};

export const setUploadingFolder = (data: UploadingFolder | null) => {
  return { type: SET_UPLOADING_FOLDER, data };
};

export const setMultiFolderUpload = (data: MultiFolderUploadState | null) => {
  return { type: SET_MULTI_FOLDER_UPLOAD, data };
};

export const updateMultiFolderProgress = (data: {
  folderName: string;
  progress: number;
  transactionId: string;
}) => {
  return { type: UPDATE_MULTI_FOLDER_PROGRESS, data };
};

export const setFoldersMetadata = (data: FolderMetadata[]) => {
  return { type: SET_FOLDERS_METADATA, data };
};

export const setDatasetPreviews = (data: any[]) => {
  return { type: SET_PREVIEWS, data };
};

export const setPreviewsLoading = (data: boolean) => {
  return { type: SET_PREVIEWS_LOADING, data };
};

export const setFolderMetadataPolling = (data: boolean) => {
  return { type: SET_FOLDER_METADATA_POLLING, data };
};

export const setLocalFolderSelections = (folderIds: string[]) => {
  return { type: SET_LOCAL_FOLDER_SELECTIONS, data: folderIds };
};

export const setCreationStatus = (data: CreationStatusResponse | null) => {
  return { type: SET_CREATION_STATUS, data };
};

export const setCreationStatusPolling = (data: boolean) => {
  return { type: SET_CREATION_STATUS_POLLING, data };
};

// Action creator for setting validation loading state
export const setValidationLoading = (isLoading: boolean) => {
  return { type: SET_VALIDATION_LOADING, data: isLoading };
};

export const checkS3Bucket = (
  newDatasetId: string,
  s3URI: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const isCalledAgain =
      getConnectStatus(getState()) === StepTileProgressStatus.COMPLETE;

    //track Data Source  event
    trackDataSourceAdded(isCalledAgain, DataSources.S3);

    try {
      dispatch(setConnectStatus(StepTileProgressStatus.IN_PROGRESS));
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/s3connect?s3_uri=${s3URI}`
      );
      const response = await axios.get(url);
      if (response.status === 202 || response.status === 200) {
        dispatch(setPreviewData([]));
        dispatch(setUploadFinished(true));
      } else {
        dispatch(setConnectStatus(StepTileProgressStatus.ACTIVE));
      }
    } catch (error: any) {
      console.log(error);
    }
  };
};

export const showFileSizeErrorModal = (): ThunkAction<
  void,
  State,
  unknown,
  any
> => {
  return async (dispatch: Dispatch) => {
    dispatch(
      setShowActivityLogModal({
        show: true,
        reason:
          "The file you selected is too large for our system. Please select a smaller file and try uploading again",
        title: "File Size Limit Exceeded",
        severity: "WARNING",
      } as any)
    );
  };
};

export const checkZip = (
  newDatasetId: string,
  file: File
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const isCalledAgain =
      getConnectStatus(getState()) === StepTileProgressStatus.COMPLETE;

    //track Data Source  event
    trackDataSourceAdded(isCalledAgain, DataSources.ZIP);

    try {
      dispatch(setUploadFinished(false));
      dispatch(setConnectStatus(StepTileProgressStatus.IN_PROGRESS));
      dispatch(setAnnotationStatus(StepTileProgressStatus.DISABLED));
      dispatch(setUploadStatus(StepTileProgressStatus.DISABLED));
      const url = composeApiURL(HOST_URL, `/ingestion/${newDatasetId}/archive`);
      const requestBody = new FormData();
      requestBody.append("archive_upload_file", file);
      dispatch(setFilesUploadProgress(0));
      dispatch(setFilesUploadSize({ length: 1, bytes: file.size }));
      const res = await axios.post(url, requestBody, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const totalLength = progressEvent.total;
          if (!!totalLength) {
            const progress = Math.min(
              Math.round((progressEvent.loaded * 100) / totalLength),
              100
            );
            dispatch(setFilesUploadProgress(progress));
          }
        },
      });
      if (res.status === 413) {
        dispatch(setFilesUploadProgress(null));
      }
      dispatch(setUploadFinished(true));
      dispatch(setConnectStatus(StepTileProgressStatus.COMPLETE));
    } catch (error: any) {
      console.log(error);
      dispatch(setUploadFinished(true));
      dispatch(setFilesUploadProgress(null));
    }
  };
};

export const checkFolder = (
  newDatasetId: string,
  files: FileList | null
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const isCalledAgain =
      getConnectStatus(getState()) === StepTileProgressStatus.COMPLETE;

    //track Data Source event
    trackDataSourceAdded(isCalledAgain, DataSources.FOLDER);

    try {
      if (!files) return;

      dispatch(setUploadFinished(false));
      const filesArray = Array.from(files);
      dispatch(setFilesUploadProgress(0));
      dispatch(
        setFilesUploadSize({
          length: filesArray.length,
          bytes: filesArray.reduce((sum, file) => sum + file.size, 0),
        })
      );
      dispatch(setConnectStatus(StepTileProgressStatus.IN_PROGRESS));
      dispatch(setAnnotationStatus(StepTileProgressStatus.DISABLED));
      dispatch(setIndexingStatus(StepTileProgressStatus.DISABLED));
      dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
      const abortController = abortControllerManager.getController();
      let url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/data_files`
      );
      const response = await axios.get(url, {
        signal: abortController.signal,
      });
      let transId: string | null = null;
      if (response.status === 200) {
        transId = response.data.trans_id;
        dispatch(setFilesUploadTransId(transId));
        dispatch(setPreviewData([]));
      }
      if (!transId) {
        throw new Error("Couldn't preform bulk upload, failed to start.");
      }

      const chunkSize = 250;
      url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/data_files/${transId}`
      );
      for (let i = 0; i < filesArray.length; i += chunkSize) {
        const chunk = filesArray.slice(i, i + chunkSize);
        const requestBody = new FormData();
        chunk.forEach((file) => {
          requestBody.append("files", file);
        });
        const isLastChunk = i + chunkSize >= filesArray.length;
        const urlWithParams = `${url}?last_chunk=${isLastChunk}`;

        const res = await axios.post(urlWithParams, requestBody, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          signal: abortController.signal,
        });
        //in case limit exceeded:
        if (res.status === 413) {
          dispatch(setFilesUploadProgress(null));
          break;
        } else {
          const percentageCompleted = Math.min(
            ((i + chunk.length) / filesArray.length) * 100,
            100
          );
          dispatch(setFilesUploadProgress(percentageCompleted));
        }
      }
      dispatch(setUploadFinished(true));
    } catch (error: any) {
      dispatch(setUploadFinished(true));
      dispatch(setFilesUploadProgress(null));
    }
  };
};

export const createNewDataset = (
  name: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const isNewCreationEnabled = getIsNewCreationEnabled(getState());

      let responseData: DatasetInitialized | null = null;
      if (isNewCreationEnabled) {
        // Use new workflow API - POST method
        const url = composeApiURL(HOST_URL, "/workflow/dataset/new");
        const response = await axios.post(url);
        responseData = response.data;
      } else {
        // Use old ingestion API - GET method with name parameter
        const url = composeApiURL(
          HOST_URL,
          `/ingestion/new_dataset?name=${name}`
        );
        responseData = await getRequest<DatasetInitialized>(url);
      }
      ReactGA.event({
        category: "Dataset",
        action: "Start to Create Dataset",
      });

      trackLinkedInConversion(LinkedInConversionId.StartToCreateDataset);

      dispatch(setNewDataset(responseData));
      //BE response contains id but type specifies dataset_id -TBD
      //@ts-ignore-next-line
      const newDatasetId = responseData.id;

      //Track New Dataset Initialization event
      trackNewDatasetIngestionEvent(
        AMP_DATASET_INGESTION_EVENT__DATASET_CREATION_INITIATED
      );

      return newDatasetId;
    } catch (error: any) {
      const errorResponse: { status: number; data: { detail: string } } =
        error.response;
      if (errorResponse.status === 400) {
        dispatch(setLimitError(errorResponse.data.detail));
      }
      return "";
    }
  };
};

export const createNewDatasetV2 = (): ThunkAction<
  void,
  State,
  unknown,
  any
> => {
  return async (dispatch: Dispatch) => {
    try {
      // Use new workflow API - POST method
      const url = composeApiURL(HOST_URL, "/workflow/dataset/new");
      const response = await axios.post(url);
      const responseData = response.data;

      ReactGA.event({
        category: "Dataset",
        action: "Start to Create Dataset - New Workflow",
      });

      trackLinkedInConversion(LinkedInConversionId.StartToCreateDataset);

      dispatch(setNewDataset(responseData));

      const newDatasetId = responseData.id;

      //Track New Dataset Initialization event
      trackNewDatasetIngestionEvent(
        AMP_DATASET_INGESTION_EVENT__DATASET_CREATION_INITIATED
      );

      return newDatasetId;
    } catch (error: any) {
      const errorResponse: { status: number; data: { detail: string } } =
        error.response;
      if (errorResponse.status === 400) {
        dispatch(setLimitError(errorResponse.data.detail));
      }
      return "";
    }
  };
};

export const fetchNewDataset = (
  uuid: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${uuid}/stats`
      );
      const response = await axios.get(url);
      const newDataset = response.data.dataset;
      dispatch(setNewDataset(newDataset));
      dispatch(setIsLoading(false));
    } catch (error: any) {
      trackFatalError(error.message, null);
      dispatch(setIsFatalError(true));
      dispatch(setNewDataset(null));
      dispatch(setIsLoading(false));
      console.log(error);
    }
  };
};

const renameDatasetById = async (
  id: string,
  name: string
): Promise<boolean> => {
  const url = composeApiURL(
    process.env.REACT_APP_API_ENDPOINT!,
    `/dataset/${id}`
  );
  const form = new FormData();
  form.append("name", name);

  const res = await axios.post(url, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res?.status === 200;
};

export const updateDatasetName = (
  name: string,
  uuid: string
): ThunkAction<Promise<boolean>, State, unknown, any> => {
  return async (dispatch, getState) => {
    const enrichingDSId = getEnrichedTargetDatasetId(getState());

    try {
      if (enrichingDSId) {
        const statsUrl = composeApiURL(
          process.env.REACT_APP_API_ENDPOINT!,
          `/dataset/${enrichingDSId}/stats`
        );
        const statsResponse = await axios.get(statsUrl);
        const sourceId = statsResponse.data?.dataset?.source_dataset_id || null;

        if (sourceId === uuid) {
          await renameDatasetById(enrichingDSId, name);
        }
      }

      await renameDatasetById(uuid, name);
      return true;
    } catch (error) {
      console.log(error);
    }
    return false;
  };
};

export const fetchEventLogs = (
  newDatasetId: string,
  isFirstCall: boolean
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      if (getStatusCallInProgress(state)) return;

      const offset = getActivityOffset(state);
      const existingLogs = getActivityLogs(state);
      const existingLastUsedData = getLastUsedData(state);
      const existingPreviewData = getPreviewData(state);
      const isNewDatasetInitializing = getIsNewDatasetInitializing(state);
      const isNewDatasetFailed = getNewDataset(state)
        ? getNewDataset(state).staus === DatasetStatusEnum.FATAL_ERROR
        : false;

      const isConntectStateInprogress =
        getConnectStatus(state) === StepTileProgressStatus.IN_PROGRESS;

      const abortController = abortControllerManager.getController();
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/status?offset=${offset}`
      );

      dispatch(setStatusCallInProgress(true));
      const fetchResponse = await axios.get(url, {
        signal: abortController.signal,
      });
      dispatch(setStatusCallInProgress(false));
      if (!fetchResponse.data) {
        return;
      }
      const fetchResponseData: any[] = fetchResponse.data;

      // Make a copy of existing logs and previews to update
      let updatedLogs: Event[] = [...existingLogs];
      let updatedPreviewData = [...existingPreviewData];
      let updatedLastUsedData: LastUsedData = { ...existingLastUsedData };

      const isFirstLog = offset === 0 && fetchResponseData.length === 1;

      if (isFirstLog && updatedLogs.length === 1) {
        return;
      }

      fetchResponseData.forEach((event: any) => {
        // handle first log
        if (event.serial === 0 && isFirstCall) {
          updatedLogs.push(event);
        }
        // Check if event serial is greater than  offset
        else if (event.serial >= offset) {
          updatedLogs.push(event);

          switch (event.event_type) {
            case "AnnotationsValid":
              dispatch(setPreviewData([]));
              updatedPreviewData = [];
              updatedLastUsedData = {
                ...updatedLastUsedData,
                annotationFiles: event.file_names,
              };
              dispatch(setAreAnnotationsFetched(false));
              dispatch(setAnnotationStatus(StepTileProgressStatus.COMPLETE));

              break;
            case "AnnotationsCleared":
              updatedPreviewData = [];
              break;

            case "AnnotationsInvalid":
              dispatch(setConnectStatus(StepTileProgressStatus.COMPLETE));
              dispatch(setAnnotationStatus(StepTileProgressStatus.ACTIVE));
              break;

            case "DatasetInitializationFailed":
            case "S3InvalidURL":
            case "S3NoAccess":
            case "S3Error":
              updatedPreviewData = [];
              dispatch(setConnectStatus(StepTileProgressStatus.ACTIVE));
              dispatch(setAnnotationStatus(StepTileProgressStatus.DISABLED));
              break;

            case "InvalidInput":
              updatedPreviewData = [];
              dispatch(setConnectStatus(StepTileProgressStatus.ACTIVE));
              dispatch(setAnnotationStatus(StepTileProgressStatus.DISABLED));
              break;
            case "ServerFailure":
              if (isConntectStateInprogress) {
                dispatch(setConnectStatus(StepTileProgressStatus.ACTIVE));
                dispatch(setAnnotationStatus(StepTileProgressStatus.DISABLED));
              }
              break;
            case "FileMediaPreview":
            case "S3MediaPreview":
              const mediaPreviewEvent: S3MediaPreview | FileMediaPreview =
                event;
              updatedPreviewData.push(mediaPreviewEvent);
              if (
                (mediaPreviewEvent.image_annotations &&
                  mediaPreviewEvent.image_annotations.length > 0) ||
                (mediaPreviewEvent.object_annotations &&
                  mediaPreviewEvent.object_annotations?.length > 0)
              ) {
                dispatch(setAreAnnotationsFetched(true));
              }
              break;

            case "PreviewReady":
              dispatch(setS3FetchProgress(-1));
              dispatch(setConnectStatus(StepTileProgressStatus.COMPLETE));
              if (getAreAnnotationsFetched(getState())) {
                dispatch(setAnnotationStatus(StepTileProgressStatus.COMPLETE));
              } else if (
                getAnnotationStatus(getState()) ===
                StepTileProgressStatus.DISABLED
              ) {
                dispatch(setAnnotationStatus(StepTileProgressStatus.ACTIVE));
              }
              break;

            case "S3Connected":
              updatedPreviewData = [];
              updatedLastUsedData = {
                ...updatedLastUsedData,
                s3Url: event.s3_url,
              };

              dispatch(setAreAnnotationsFetched(false));
              break;

            case "DatasetStatus":
              dispatch(setAnnotationStatus(StepTileProgressStatus.COMPLETE));
              dispatch(setConnectStatus(StepTileProgressStatus.COMPLETE));
              dispatch(setUploadStatus(StepTileProgressStatus.COMPLETE));
              switch (event.status) {
                case "DatasetStatus.READY":
                  if (isNewDatasetInitializing) {
                    trackNewDatasetIngestionEvent(
                      AMP_DATASET_INGESTION_EVENT__DATASET_CREATION_SUCCESS
                    );
                  }

                  dispatch(setIndexingStatus(StepTileProgressStatus.COMPLETE));
                  break;

                case "DatasetStatus.FATAL_ERROR":
                  if (isNewDatasetInitializing || isNewDatasetFailed) {
                    trackFatalError(
                      event.reason || "",
                      getPreviewDataSource(state)
                    );
                  }
                  dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
                  dispatch(setIndexingStatus(StepTileProgressStatus.DISABLED));
                  break;

                case "DatasetStatus.INDEXING":
                  dispatch(
                    setIndexingStatus(StepTileProgressStatus.IN_PROGRESS)
                  );
                  break;
              }

              break;

            case "FatalDatasetStatus":
              if (isNewDatasetInitializing || isNewDatasetFailed) {
                trackFatalError(
                  event.reason || "",
                  getPreviewDataSource(state)
                );
              }
              break;

            case "LimitExceeded":
              const eventType: LimitExceeded["limit_type"] = event.limit_type;
              const userInitiatedCreation = getUserInitiatedCreation(state);

              if (eventType === "MAX_USER_QUOTA") {
                const dataSource = getPreviewDataSource(state);
                let attemptedImageCount;

                if (dataSource === DataSources.FOLDER) {
                  attemptedImageCount = getFilesUploadSize(state).length;
                } else {
                  attemptedImageCount = event.actual_value;
                }

                dispatch(
                  setQuotaLimitModalData({
                    show: userInitiatedCreation,
                    attemptedImageCount: attemptedImageCount,
                  })
                );

                //track Quota limi exceeded event
                trackNewDatasetIngestionEvent(
                  AMP_DATASET_INGESTION_EVENT__QUOTA_LIMIT_EXCEEDED,
                  {
                    [AMP_DATASET_CREATION_PROPERTY__UPLOAD_SOURCE]: dataSource,
                    [AMP_DATASET_CREATION_PROPERTY__ATTEMPTED_UPLOAD_COUNT]:
                      attemptedImageCount,
                    [AMP_DATASET_CREATION_PROPERTY__ALLOWED_UPLOAD_COUNT]:
                      getUserQuota(state)?.availableQuota,
                  }
                );
              }
              dispatch(setLimitExceededDetails(event));
              dispatch(setUploadFinished(true));
              dispatch(setConnectStatus(StepTileProgressStatus.COMPLETE));
              dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));

              break;

            case "LimitNotExceeded":
              dispatch(setConnectStatus(StepTileProgressStatus.COMPLETE));
              if (getUserInitiatedCreation(state)) {
                dispatch(startProcess(newDatasetId));
              } else {
                dispatch(setUploadFinished(true));
                dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
              }
              break;

            case "FilesUploadStart":
              updatedPreviewData = [];
              dispatch(setFilesUploadTransId(event.transaction_id));
              break;

            case "FilesChunkUploaded":
              updatedLastUsedData = {
                ...updatedLastUsedData,
                folderName: event.folder_name,
              };

              break;

            case "DataSourceEvent":
              updatedPreviewData = [];
              if (getIsUsageReportEnabled(state)) {
                dispatch(checkUserQuota());
              }
              let source: DataSourceEvent["source_type"] = event.source_type;
              switch (source) {
                case "FOLDER":
                  dispatch(setPreviewDataSource(DataSources.FOLDER));
                  break;
                case "BUCKET":
                  dispatch(setPreviewDataSource(DataSources.S3));
                  break;
                case "ZIP":
                  dispatch(setPreviewDataSource(DataSources.ZIP));
                  break;
              }
          }
        }
      });

      // For the case in which the previous upload was interrupted
      if (offset === 0) {
        const hasDataSourceEvent = fetchResponseData.some(
          (event) =>
            event.event_type === "DataSourceEvent" &&
            event.source_type &&
            event.source_type === "FOLDER"
        );
        const hasFilesUploadCompleted = fetchResponseData.some(
          (event) => event.event_type === "FilesUploadCompleted"
        );
        if (hasDataSourceEvent && !hasFilesUploadCompleted) {
          dispatch(setConnectStatus(StepTileProgressStatus.ACTIVE));
          dispatch(setAnnotationStatus(StepTileProgressStatus.DISABLED));
          dispatch(setUploadStatus(StepTileProgressStatus.FAILED));
        }
      }

      // Sort logs by serial in descending order
      updatedLogs.sort((a, b) => b.serial - a.serial);

      dispatch(setActivityLogs(updatedLogs));
      dispatch(setPreviewData(updatedPreviewData));

      if (!isNewDatasetInitializing) {
        dispatch(setLastUsedData(updatedLastUsedData));
      }

      const newOffset =
        fetchResponseData.length > 0
          ? fetchResponseData[fetchResponseData.length - 1].serial + 1
          : offset;
      dispatch(setActivityOffset(newOffset));
    } catch (error: any) {
      trackFatalError(error.message, null);
      console.error("Error fetching event logs:", error);
      dispatch(setS3FetchProgress(-1));
    }
  };
};

export const uploadAnnotations = (
  newDatasetId: string,
  files: File[]
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const isCalledAgain =
      getAnnotationStatus(getState()) === StepTileProgressStatus.COMPLETE;

    //track Annotation selection
    trackAnnotation(isCalledAgain);

    try {
      dispatch(setAnnotationStatus(StepTileProgressStatus.IN_PROGRESS));
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/annotations`
      );

      const requestBody = new FormData();
      files.forEach((file) => requestBody.append("files", file));

      axios
        .post(url, requestBody, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .catch((error) => {
          console.error("File upload failed", error);
        });
    } catch (error: any) {
      console.log(error);
    }
  };
};

export const skipAnnotations = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    dispatch(clearAnnotations(newDatasetId) as any);
    dispatch(setAnnotationStatus(StepTileProgressStatus.COMPLETE));
  };
};

export const clearAnnotations = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/annotations`
      );
      axios.delete(url).catch((error) => {
        console.error("clear annotations failed", error);
      });
    } catch (error: any) {
      console.log(error);
    }
  };
};

export const startProcess = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    try {
      const state = getState();
      const dataSource = getPreviewDataSource(state);
      if (dataSource === DataSources.S3) {
        dispatch(processS3(newDatasetId) as any);
      } else if (dataSource === DataSources.FOLDER) {
        dispatch(processFiles(newDatasetId) as any);
      } else if (dataSource === DataSources.ZIP) {
        dispatch(processZip(newDatasetId) as any);
      }

      trackNewDatasetIngestionEvent(
        AMP_DATASET_INGESTION_EVENT__INDEXING_STARTED,
        {
          [AMP_DATASET_CREATION_PROPERTY__UPLOAD_SOURCE]: dataSource,
        }
      );
    } catch (error: any) {
      console.log(error);
    }
  };
};

export const processZip = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setUploadStatus(StepTileProgressStatus.COMPLETE));
      dispatch(setIndexingStatus(StepTileProgressStatus.IN_PROGRESS));
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/process_archive`
      );
      const response = await axios.get(url);
      if (response.status !== 202) {
        //handle errors -TBD
        dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
        dispatch(setIndexingStatus(StepTileProgressStatus.ACTIVE));
      }
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 413) {
        dispatch(showFileSizeErrorModal() as any);
      }
      dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
    }
  };
};

export const processFiles = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    try {
      dispatch(setUploadStatus(StepTileProgressStatus.COMPLETE));
      dispatch(setIndexingStatus(StepTileProgressStatus.IN_PROGRESS));
      const state = getState();
      const transId = getFilesUploadTransId(state);
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/process_files/${transId}`
      );
      const response = await axios.get(url);
      if (response.status !== 202) {
        //handle errors -TBD
        dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
        dispatch(setIndexingStatus(StepTileProgressStatus.ACTIVE));
      }
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 413) {
        dispatch(showFileSizeErrorModal() as any);
      }
      dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
    }
  };
};

export const processS3 = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setUploadStatus(StepTileProgressStatus.COMPLETE));
      dispatch(setIndexingStatus(StepTileProgressStatus.IN_PROGRESS));
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/process_s3`
      );

      const response = await axios.get(url);

      if (response.status !== 202) {
        //handle errors -TBD
        dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
        dispatch(setIndexingStatus(StepTileProgressStatus.ACTIVE));
      }
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 413) {
        dispatch(showFileSizeErrorModal() as any);
      }
      dispatch(setUploadStatus(StepTileProgressStatus.ACTIVE));
    }
  };
};

export const setShowActivityLogModal = (data: ActivityLogShowModalType) => {
  return { type: SET_SHOW_ACTIVITY_LOG_MODAL, data };
};

export const fetchCVATData = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setIsCvatLoading(true));
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/list_cvat`
      );
      const response = await axios.get(url);
      dispatch(setCVATData(response.data as any));
      dispatch(setIsCvatLoading(false));
    } catch (error: any) {
      console.log(error);
      dispatch(setIsCvatLoading(false));
    }
  };
};

export const importCVATData = (
  newDatasetId: string,
  cvatData: { id: number; type: CVATTypes } | null
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setAnnotationStatus(StepTileProgressStatus.IN_PROGRESS));
      const queryParams =
        cvatData?.type === CVATTypes.TASK
          ? "?task_id=" + cvatData?.id
          : cvatData?.type === CVATTypes.PROJECT
            ? "?project_id=" + cvatData?.id
            : "";
      const url = composeApiURL(
        HOST_URL,
        `/ingestion/${newDatasetId}/import_cvat${queryParams}`
      );
      await axios.get(url);
    } catch (error: any) {
      console.log(error);
    }
  };
};

// New Dataset Creation Workflow Action Creators
export const startFolderUploadTransaction = (
  newDatasetId: string,
  folderName: string,
  files: File[]
): ThunkAction<Promise<void>, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setFolderUploadStatus(FolderUploadStatus.ACTIVE));

      const abortController = abortControllerManager.getController();

      const encodedFolderName = encodeURIComponent(folderName);
      const url = composeApiURL(
        HOST_URL,
        `/dataset/${newDatasetId}/data/folder_upload_transaction?folder_name=${encodedFolderName}&total_files=${files.length}`
      );

      const response = await axios.get<{ trans_id: string }>(url, {
        signal: abortController.signal,
      });
      const responseData: { trans_id: string } = response.data;
      const transactionId = responseData.trans_id;

      if (!transactionId) {
        console.error("no transaction id");
        dispatch(setUploadingFolder(null));
        dispatch(setFolderUploadStatus(FolderUploadStatus.FAILED));
        return;
      }

      dispatch(
        setUploadingFolder({ name: folderName, transactionId, progress: 0 })
      );
      dispatch(uploadFolderFiles(newDatasetId, transactionId, files) as any);
    } catch (error: any) {
      dispatch(setFolderUploadStatus(FolderUploadStatus.FAILED));
      dispatch(setUploadingFolder(null));
      throw error;
    }
  };
};

// Upload folder files in chunks
export const uploadFolderFiles = (
  newDatasetId: string,
  transId: string,
  files: File[]
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    try {
      const filesArray = Array.from(files);
      const chunkSize = 250;
      const abortController = abortControllerManager.getController();

      const url = composeApiURL(
        HOST_URL,
        `/dataset/${newDatasetId}/data/folder_upload/${transId}`
      );

      for (let i = 0; i < filesArray.length; i += chunkSize) {
        const chunk = filesArray.slice(i, i + chunkSize);
        const formData = new FormData();
        chunk.forEach((file) => formData.append("files", file));

        const isLastChunk = i + chunkSize >= filesArray.length;
        const urlWithParams = `${url}?last_chunk=${isLastChunk ? 1 : 0}`;

        await axios.post(urlWithParams, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          signal: abortController.signal,
        });

        // Update progress based on completed chunks
        const percentageCompleted = Math.min(
          ((i + chunk.length) / filesArray.length) * 100,
          100
        );
        const uploadingFolder = getUploadingFolder(getState());
        if (uploadingFolder) {
          dispatch(
            setUploadingFolder({
              ...uploadingFolder,
              progress: Math.round(percentageCompleted),
            })
          );
        }
      }

      // Mark as completed when all chunks are uploaded
      dispatch(setUploadingFolder(null));
      dispatch(setFolderUploadStatus(FolderUploadStatus.COMPLETED));
    } catch (error: any) {
      console.error("Folder upload failed:", error);
      dispatch(setUploadingFolder(null));
      dispatch(setFolderUploadStatus(FolderUploadStatus.FAILED));
    }
  };
};

// New action for handling multiple folder upload transactions
export const startMultipleFolderUploadTransaction = (
  newDatasetId: string,
  folderGroups: Array<{ folderName: string; files: File[] }>
): ThunkAction<Promise<void>, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setFolderUploadStatus(FolderUploadStatus.ACTIVE));
      const abortController = abortControllerManager.getController();

      // Initialize multi-folder upload state
      const initialFolders: UploadingFolder[] = folderGroups.map((group) => ({
        name: group.folderName,
        transactionId: "", // Will be set after getting transaction ID
        progress: 0,
      }));

      dispatch(
        setMultiFolderUpload({
          folders: initialFolders,
          overallProgress: 0,
          status: FolderUploadStatus.ACTIVE,
        })
      );

      // Create transaction for each folder and start uploads in parallel
      const uploadPromises = folderGroups.map(async (group) => {
        try {
          // Get transaction ID for this folder
          const encodedFolderName = encodeURIComponent(group.folderName);
          const url = composeApiURL(
            HOST_URL,
            `/dataset/${newDatasetId}/data/folder_upload_transaction?folder_name=${encodedFolderName}&total_files=${group.files.length}`
          );

          const response = await axios.get<{ trans_id: string }>(url, {
            signal: abortController.signal,
          });
          const responseData: { trans_id: string } = response.data;
          const transactionId = responseData.trans_id;

          if (!transactionId) {
            throw new Error(
              `Failed to get transaction ID for folder: ${group.folderName}`
            );
          }

          // Update the folder with its transaction ID
          dispatch(
            updateMultiFolderProgress({
              folderName: group.folderName,
              progress: 0,
              transactionId,
            })
          );

          // Upload files for this folder
          await uploadMultiFolderFiles(
            dispatch,
            newDatasetId,
            transactionId,
            group.folderName,
            group.files
          );
        } catch (error: any) {
          throw error;
        }
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      // Mark all uploads as completed
      dispatch(setMultiFolderUpload(null));
      dispatch(setFolderUploadStatus(FolderUploadStatus.COMPLETED));
    } catch (error: any) {
      dispatch(setFolderUploadStatus(FolderUploadStatus.FAILED));
      dispatch(setMultiFolderUpload(null));
      throw error;
    }
  };
};

// Helper function to upload files for a single folder in multi-folder scenario
const uploadMultiFolderFiles = async (
  dispatch: Dispatch,
  newDatasetId: string,
  transactionId: string,
  folderName: string,
  files: File[]
) => {
  const abortController = abortControllerManager.getController();
  const filesArray = Array.from(files);
  const chunkSize = 50;
  const url = composeApiURL(
    HOST_URL,
    `/dataset/${newDatasetId}/data/folder_upload/${transactionId}`
  );

  for (let i = 0; i < filesArray.length; i += chunkSize) {
    const chunk = filesArray.slice(i, i + chunkSize);
    const formData = new FormData();
    const isLastChunk = i + chunkSize >= filesArray.length;

    chunk.forEach((file) => {
      formData.append("files", file);
    });

    await axios.post(url, formData, {
      params: { last_chunk: isLastChunk },
      signal: abortController.signal,
    });

    // Update progress for this specific folder
    const percentageCompleted = Math.min(
      ((i + chunk.length) / filesArray.length) * 100,
      100
    );

    dispatch(
      updateMultiFolderProgress({
        folderName,
        progress: Math.round(percentageCompleted),
        transactionId,
      })
    );
  }
};

const folderStatusMap: Record<BackendFolderStatus, FolderStatus> = {
  uploading: "uploading",
  validating: "validating",
  valid: "valid",
  ready: "valid", // Don't show ready status in table - map to valid
  invalid: "invalid",
};

const transformFolderMetadata = (
  folders: Array<
    Omit<FolderMetadata, "status" | "backend_status"> & {
      status: BackendFolderStatus;
    }
  >
): FolderMetadata[] => {
  return folders.map((folder) => ({
    ...folder,
    backend_status: folder.status, // Store original backend status
    status: folderStatusMap[folder.status], // frontend status
  }));
};

export const fetchFolderMetadata = (
  newDatasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    const abortController = abortControllerManager.getController();
    try {
      const url = composeApiURL(
        HOST_URL,
        `/dataset/${newDatasetId}/folders_metadata`
      );
      const responseData = await getRequest<{ folders: FolderMetadata[] }>(
        url,
        { signal: abortController.signal }
      );
      dispatch(
        setFoldersMetadata(transformFolderMetadata(responseData.folders))
      );
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch folder metadata";
      console.error(message);
    }
  };
};

// Polling interval ID storage
let pollingIntervalId: NodeJS.Timeout | null = null;

// Start polling folders metadata
export const startFolderMetadataPolling = (
  datasetId: string,
  intervalMs: number = 5000
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const isAlreadyPolling = getFolderMetadataPolling(state);

    // If already polling for the same dataset, don't start a new one
    if (isAlreadyPolling && pollingIntervalId) {
      return;
    }

    // Clear any existing polling interval
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }

    // Set polling state to true
    dispatch(setFolderMetadataPolling(true));

    // Start polling
    const poll = async () => {
      const isPolling = getFolderMetadataPolling(getState());
      if (!isPolling) return;

      await dispatch(fetchFolderMetadata(datasetId) as any);

      const folders = getFolderMetadata(getState()) || [];
      const allDone = folders.every((f) =>
        ["ready", "invalid"].includes(f.backend_status)
      );
      if (allDone && folders.length > 0) {
        dispatch(stopFolderMetadataPolling() as any);
      }
    };

    // Initial fetch
    await poll();

    // Set up interval
    pollingIntervalId = setInterval(poll, intervalMs);
  };
};

// Stop polling folders metadata
export const stopFolderMetadataPolling = (): ThunkAction<
  void,
  State,
  unknown,
  any
> => {
  return async (dispatch: Dispatch) => {
    // Clear polling interval
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }

    // Set polling state to false
    dispatch(setFolderMetadataPolling(false));
  };
};

export const selectDatasetFolders = (
  newDatasetId: string,
  folderIds: string[]
): ThunkAction<Promise<boolean>, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const folders = getFolderMetadata(getState());
      const allFoldersReady = folderIds.every(
        (id) => folders.find((folder) => folder.id === id)?.status === "valid"
      );

      const url = composeApiURL(
        HOST_URL,
        `/dataset/${newDatasetId}/select_folders`
      );

      const response = await axios.post(url, { folder_ids: folderIds });

      if (response.data?.status === "success" && allFoldersReady) {
        return true;
      }

      return false;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to select folders";
      console.error("Select folders error:", errorMessage);
      return false;
    }
  };
};

// Fetch dataset previews
export const fetchDatasetPreviews = (
  datasetId: string
): ThunkAction<Promise<boolean>, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    dispatch(setPreviewsLoading(true));

    try {
      const url = composeApiURL(HOST_URL, `/dataset/${datasetId}/previews`);

      const response = await axios.get(url);
      const previews = response.data?.previews || [];

      dispatch(setDatasetPreviews(previews));

      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to fetch previews";
      console.error("Fetch previews error:", errorMessage);

      return false;
    } finally {
      dispatch(setPreviewsLoading(false));
    }
  };
};

// Process dataset using the new /process endpoint
export const processDataset = (
  datasetId: string
): ThunkAction<Promise<boolean>, State, unknown, any> => {
  return async () => {
    try {
      const url = composeApiURL(HOST_URL, `/dataset/${datasetId}/process`);

      const response = await axios.get(url);

      if (response.status === 200 || response.status === 202) {
        return true;
      } else {
        console.error("Process dataset API call failed");
        return false;
      }
    } catch (error: any) {
      console.error("Process dataset error:", error);
      return false;
    }
  };
};

// Transform backend response to desired format
const transformCreationStatus = (response: {
  primary_status: CreationStatusResponse["primaryStatus"];
  fatal_error: string | null;
  secondary_status: { [key: string]: number };
}): CreationStatusResponse => {
  // Use the exact same order as backend's DatasetStageProgress.build_progress_dict
  // From vldbaccess/base.py
  const backendStepOrder = [
    "UPLOADING",
    "INITIALIZING",
    "PRE_PROCESSING",
    "INDEXING",
    "SAVING",
  ];

  const { secondary_status } = response;

  // Sort secondary_status based on backend's predefined order
  const secondaryStatus: SecondaryStatusItem[] = backendStepOrder
    .filter((status) => status in secondary_status)
    .map((status) => ({ status, progress: secondary_status[status] || 0 }));

  return {
    primaryStatus: response.primary_status,
    fatalError: response.fatal_error,
    secondaryStatus: secondaryStatus,
  };
};

// Fetch creation status from the /creation-status endpoint
const fetchCreationStatus = (
  datasetId: string
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const abortController = abortControllerManager.getController();
      const url = composeApiURL(
        HOST_URL,
        `/datasets/${datasetId}/creation-status`
      );
      const response = await axios.get(url, {
        signal: abortController.signal,
      });
      const transformedData = transformCreationStatus(response.data);
      dispatch(setCreationStatus(transformedData));
    } catch (error: any) {
      // Don't log errors if request was aborted
      if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
        return;
      }
      const message =
        error.response?.data?.message || "Failed to fetch creation status";
      console.error(message);
      dispatch(
        setCreationStatus({
          primaryStatus: "FATAL_ERROR",
          fatalError: message,
          secondaryStatus: [],
        })
      );
    }
  };
};

// Polling interval ID storage for creation status
let creationStatusPollingIntervalId: NodeJS.Timeout | null = null;

// Start polling creation status
export const startCreationStatusPolling = (
  datasetId: string,
  intervalMs: number = 2000
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    // Clear any existing polling interval
    if (creationStatusPollingIntervalId) {
      clearInterval(creationStatusPollingIntervalId);
      creationStatusPollingIntervalId = null;
    }

    // Set polling state to true
    dispatch(setCreationStatusPolling(true));

    // Start polling
    const poll = async () => {
      const state = getState();
      const isPolling = getCreationStatusPolling(state);
      const isNewDatasetReady = getIsNewDatasetReady(state);

      if (!isPolling) {
        return;
      }

      await dispatch(fetchCreationStatus(datasetId) as any);

      const isCreationStatusReady =
        getCreationStatus(state)?.primaryStatus === "READY";
      const isCreationStatusFatalError =
        getCreationStatus(state)?.primaryStatus === "FATAL_ERROR";
      // Check if we should stop polling based on status
      if (
        isNewDatasetReady ||
        isCreationStatusReady ||
        isCreationStatusFatalError
      ) {
        dispatch(stopCreationStatusPolling() as any);
        dispatch(fetchNewDataset(datasetId) as any);
      }
    };

    // Initial fetch
    await poll();

    // Set up interval
    creationStatusPollingIntervalId = setInterval(poll, intervalMs);
  };
};

// Action creator for setting invalid folders
export const setInvalidFolders = (invalidFolders: FolderMetadata[]) => ({
  type: SET_INVALID_FOLDERS,
  data: invalidFolders,
});

// Stop polling creation status
export const stopCreationStatusPolling = (): ThunkAction<
  void,
  State,
  unknown,
  any
> => {
  return async (dispatch: Dispatch) => {
    // Clear polling interval
    if (creationStatusPollingIntervalId) {
      clearInterval(creationStatusPollingIntervalId);
      creationStatusPollingIntervalId = null;
    }

    // Set polling state to false
    dispatch(setCreationStatusPolling(false));
  };
};
